/**
 * @fileoverview Shared report-parameter model + pure resolvers for the data report
 * parameter mechanism (fixed + relative modes).
 * @module report-template-resolver/reportParameters
 *
 * Responsibilities:
 * - Define the parameter vocabulary (ReportParameter, ParameterBinding,
 *   ResolvedParameterValues, RelativeDateToken) shared by tenant-web services,
 *   system-web services, the tenant-api Zod schemas, and the migration/schedule workers.
 * - Provide the SINGLE implementation (DRY — no client/server mirror) of the pure
 *   resolvers: resolveRelativeToken, resolveParameterBindings,
 *   mergeFiltersWithParameters, validateReportParameters.
 *
 * Dependencies:
 * - none (pure types + pure functions; no Dexie, no API, no React).
 *
 * Architecture:
 * - PURE functions only. Callers pass plain values read from already-synced records.
 * - Consumed by BOTH web (via @firesquirrel/core-domain thin re-export) AND the
 *   API/worker (via @firesquirrel/core re-export of this package), so scheduled-run
 *   parameter resolution can never drift from interactive-run resolution.
 *
 * Notes:
 * - Relative tokens resolve against an explicit runDate — never now() internally —
 *   so the same (bindings, runDate) input always yields identical output.
 * - Date-range tokens resolve to a [startIso, endIso] tuple where start is 00:00:00.000
 *   and end is 23:59:59.999 of the boundary days (server-local time, matching the
 *   existing computeNextRunAt convention).
 */

// ---------------------------------------------------------------------------
// Parameter vocabulary
// ---------------------------------------------------------------------------

/**
 * @contract ParameterType
 * Purpose: The data type of a report parameter — drives the UI widget, validation,
 *   and the shape of its resolved value.
 * Fields: 'date' | 'dateRange' | 'lookup' | 'inspectionType' | 'entity' |
 *   'account' | 'text' | 'number' | 'boolean'
 * Rules: 'date' resolves to an ISO date string; 'dateRange' resolves to a
 *   [startIso, endIso] tuple; all other types resolve to primitives/string[].
 * Sync: N/A (type-only); persisted on ReportDefinitionLocal.parameters[].type.
 */
export type ParameterType =
  | 'date'
  | 'dateRange'
  | 'lookup'
  | 'inspectionType'
  | 'entity'
  | 'account'
  | 'text'
  | 'number'
  | 'boolean';

/**
 * @contract ParameterMode
 * Purpose: How a parameter's value is supplied.
 * Fields: 'fixed' (concrete value baked in) | 'relative' (a RelativeDateToken
 *   resolved at run time — only valid for 'date' / 'dateRange' types).
 * Sync: Persisted on ReportParameter.mode and ParameterBinding.mode.
 */
export type ParameterMode = 'fixed' | 'relative';

/**
 * @contract RelativeDateToken
 * Purpose: A named relative-date expression resolved against an arbitrary run date.
 * Fields: One of 12 tokens (see union).
 * Rules: Only valid for parameters with type 'date'/'dateRange' and mode 'relative'.
 * Sync: Persisted in ParameterBinding.token.
 */
export type RelativeDateToken =
  | 'previousMonth'
  | 'currentMonth'
  | 'nextMonth'
  | 'previousQuarter'
  | 'currentQuarter'
  | 'last30Days'
  | 'last7Days'
  | 'yearToDate'
  | 'previousYear'
  | 'currentYear'
  | 'previousDay'
  | 'currentDay';

/**
 * @contract ReportParameter
 * Purpose: Declares a runtime parameter on a data report. The user (or a schedule
 *   binding) supplies a value at run time; the value is applied as a filter.
 * Fields: parameterId, label, type, mode, required, defaultValue?, options?, config?
 * Rules: parameterId is unique within a report definition. mode 'relative' is only
 *   valid for 'date'/'dateRange' types. 'lookup' type requires options.lookupType.
 * Sync: stored on ReportDefinitionLocal.parameters (Dexie + MongoDB sync).
 */
export interface ReportParameter {
  parameterId: string;
  label: string;
  type: ParameterType;
  mode: ParameterMode;
  required: boolean;
  defaultValue?: unknown;
  /**
   * The report field this parameter filters at run time (e.g.
   * `contacts.createdAt`). When set and the parameter resolves to a value,
   * mergeFiltersWithParameters synthesizes a filter on this field — a bare
   * parameter with no filterField and no column/filter binding applies
   * nothing (the resolved value is inert).
   */
  filterField?: string;
  options?: {
    /** For type='lookup': the lookupType loaded via listLookupValues. */
    lookupType?: string;
    /** For type='inspectionType'/'entity'/'account': the source descriptor. */
    source?: 'lookup' | 'inspectionType' | 'entity' | 'account';
    /** For type='entity'/'account': the entityCategory filter. */
    entityCategory?: string;
    /** Whether multi-select is allowed (default false). */
    multi?: boolean;
  };
  /** Type-specific config (e.g. date format, number min/max). */
  config?: Record<string, unknown>;
}

/**
 * @contract ParameterBinding
 * Purpose: Binds a report parameter to a value on a schedule. Either fixed
 *   (concrete value) or relative (token resolved at run time).
 * Fields: parameterId, mode, value?, token?
 * Rules: mode='fixed' requires value; mode='relative' requires token and is only
 *   valid for date/dateRange parameters.
 * Sync: stored on ReportScheduleLocal.parameterBindings (Dexie + MongoDB sync).
 */
export interface ParameterBinding {
  parameterId: string;
  mode: ParameterMode;
  /** Present when mode='fixed'. Shape depends on the parameter's type. */
  value?: unknown;
  /** Present when mode='relative'. */
  token?: RelativeDateToken;
}

/**
 * @contract ResolvedParameterValues
 * Purpose: Output of resolving parameter bindings against a run date (or a user's
 *   interactive input). Keyed by parameterId.
 * Fields: values
 * Rules: Every required parameter must have an entry. 'date' values are ISO strings;
 *   'dateRange' values are [startIso, endIso] tuples.
 * Sync: runtime-only; never persisted.
 */
export interface ResolvedParameterValues {
  values: Record<string, unknown>;
}

/**
 * @contract ReportConfigFilter
 * Purpose: A single report filter clause. When parameterId is set, the filter's
 *   value is supplied at run time by the named parameter and `value` is ignored.
 * Fields: field, operator, value, parameterId?
 * Rules: Named ReportConfigFilter (not ReportFilter) to avoid colliding with the
 *   existing cube-style ReportFilter ({member, operator, values}) in local-db.
 * Sync: stored on ReportConfig.filters.
 */
export interface ReportConfigFilter {
  field: string;
  operator: string;
  value: any;
  parameterId?: string;
}

// ---------------------------------------------------------------------------
// Pure resolvers
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function endOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(23, 59, 59, 999);
  return out;
}

function startOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1, 0, 0, 0, 0);
}

function endOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0, 23, 59, 59, 999);
}

/**
 * Resolve a relative-date token against a run date.
 *
 * @process resolveRelativeToken
 * Steps: Compute the calendar boundary (month/quarter/year/day) relative to runDate.
 * Inputs: token, runDate.
 * Outputs: [startIso, endIso] covering the full boundary period.
 * Failure Handling: throws on unknown token.
 * Idempotency: REQUIRED — pure function.
 *
 * @param token - The relative token.
 * @param runDate - The reference date (schedule run time or preview date).
 * @returns [startIso, endIso] tuple. Single-day tokens return start===end boundaries.
 */
export function resolveRelativeToken(
  token: RelativeDateToken,
  runDate: Date,
): [string, string] {
  const y = runDate.getFullYear();
  const m = runDate.getMonth();

  switch (token) {
    case 'previousMonth': {
      const start = startOfMonth(y, m - 1);
      return [start.toISOString(), endOfMonth(start.getFullYear(), start.getMonth()).toISOString()];
    }
    case 'currentMonth':
      return [startOfMonth(y, m).toISOString(), endOfMonth(y, m).toISOString()];
    case 'nextMonth': {
      const start = startOfMonth(y, m + 1);
      return [start.toISOString(), endOfMonth(start.getFullYear(), start.getMonth()).toISOString()];
    }
    case 'previousQuarter': {
      const q = Math.floor(m / 3) - 1;
      const start = startOfMonth(y, q * 3);
      return [start.toISOString(), endOfMonth(start.getFullYear(), start.getMonth() + 2).toISOString()];
    }
    case 'currentQuarter': {
      const q = Math.floor(m / 3);
      return [startOfMonth(y, q * 3).toISOString(), endOfMonth(y, q * 3 + 2).toISOString()];
    }
    case 'last30Days':
      return [startOfDay(new Date(runDate.getTime() - 30 * DAY_MS)).toISOString(), endOfDay(runDate).toISOString()];
    case 'last7Days':
      return [startOfDay(new Date(runDate.getTime() - 7 * DAY_MS)).toISOString(), endOfDay(runDate).toISOString()];
    case 'yearToDate':
      return [new Date(y, 0, 1, 0, 0, 0, 0).toISOString(), endOfDay(runDate).toISOString()];
    case 'previousYear':
      return [new Date(y - 1, 0, 1, 0, 0, 0, 0).toISOString(), new Date(y - 1, 11, 31, 23, 59, 59, 999).toISOString()];
    case 'currentYear':
      return [new Date(y, 0, 1, 0, 0, 0, 0).toISOString(), new Date(y, 11, 31, 23, 59, 59, 999).toISOString()];
    case 'previousDay': {
      const d = new Date(runDate.getTime() - DAY_MS);
      return [startOfDay(d).toISOString(), endOfDay(d).toISOString()];
    }
    case 'currentDay':
      return [startOfDay(runDate).toISOString(), endOfDay(runDate).toISOString()];
    default:
      throw new Error(`Unknown relative date token: ${String(token)}`);
  }
}

/**
 * Resolve a schedule's parameter bindings against a run date. Shared by the
 * interactive-run preview hook AND the scheduled-report worker — identical output
 * for identical input is the parity invariant.
 *
 * @process resolveParameterBindings
 * Steps:
 * 1. Index bindings by parameterId.
 * 2. For each declared parameter: resolve its binding (fixed value verbatim;
 *    relative token → resolveRelativeToken; date params get the range start).
 * 3. Fall back to the parameter's defaultValue when a non-required binding is absent.
 * Inputs: bindings (schedule.parameterBindings), parameters (report.parameters), runDate.
 * Outputs: ResolvedParameterValues.
 * Failure Handling: throws when a required parameter has no binding, when a relative
 *   binding targets a non-date parameter, or when mode='relative' lacks a token.
 * Idempotency: REQUIRED — same (bindings, parameters, runDate) → same output.
 */
export function resolveParameterBindings(
  bindings: ParameterBinding[],
  parameters: ReportParameter[],
  runDate: Date,
): ResolvedParameterValues {
  const byParameterId = new Map(bindings.map((b) => [b.parameterId, b]));
  const values: Record<string, unknown> = {};

  /**
   * Resolve a parameter's defaultValue into the same shape an explicit binding
   * produces. Relative-mode date/dateRange parameters store a token (e.g.
   * 'previousMonth') which must be evaluated against runDate — copying it
   * verbatim would compare row dates against a literal token string and empty
   * every report. Non-relative defaults pass through unchanged.
   */
  const resolveParamDefault = (param: ReportParameter): unknown => {
    const dv = param.defaultValue;
    if (
      (param.type === 'date' || param.type === 'dateRange') &&
      param.mode === 'relative' &&
      typeof dv === 'string'
    ) {
      const [start, end] = resolveRelativeToken(dv as RelativeDateToken, runDate);
      return param.type === 'date' ? start : [start, end];
    }
    // Fixed defaults can persist the same non-tuple range shapes as explicit
    // bindings — normalize so an `{start,end}`/`'a.b'` default doesn't fall
    // through to an equality compare and empty the scheduled report.
    return normalizeParamValue(param, dv);
  };

  for (const param of parameters) {
    const binding = byParameterId.get(param.parameterId);

    if (!binding) {
      if (param.defaultValue !== undefined) {
        values[param.parameterId] = resolveParamDefault(param);
        continue;
      }
      if (param.required) {
        throw new Error(`Required parameter '${param.parameterId}' has no binding`);
      }
      continue;
    }

    if (binding.mode === 'relative') {
      if (param.type !== 'date' && param.type !== 'dateRange') {
        throw new Error(
          `Parameter '${param.parameterId}' (type '${param.type}') cannot use relative mode`,
        );
      }
      if (!binding.token) {
        throw new Error(`Relative binding for '${param.parameterId}' is missing a token`);
      }
      const [start, end] = resolveRelativeToken(binding.token, runDate);
      values[param.parameterId] = param.type === 'date' ? start : [start, end];
      continue;
    }

    const isEmptyValue =
      binding.value === undefined ||
      binding.value === null ||
      binding.value === '' ||
      (Array.isArray(binding.value) && binding.value.length === 0);

    if (isEmptyValue) {
      if (param.defaultValue !== undefined && param.defaultValue !== null && param.defaultValue !== '') {
        values[param.parameterId] = resolveParamDefault(param);
        continue;
      }
      if (param.required) {
        throw new Error(`Required parameter '${param.parameterId}' has no value`);
      }
      continue;
    }

    values[param.parameterId] = normalizeParamValue(param, binding.value);
  }

  return { values };
}

/**
 * Normalize a fixed-mode binding value for date/dateRange parameters. Run-page
 * prompts may persist the range as an `{start,end}`/`{startDate,endDate}`
 * object or an `'ISOstart.ISOend'`-joined string; `mergeFiltersWithParameters`
 * only upgrades `eq` to `inDateRange` for `[start, end]` tuples — any other
 * range shape falls through to an equality compare and empties the report.
 * Non-range shapes and non-date params pass through unchanged.
 */
/**
 * Convert any persisted date-range shape — `{start,end}`/`{startDate,endDate}`
 * objects, `'ISOstart.ISOend'` joined strings, or `[start, end]` tuples — into
 * a `[start, end]` string tuple. Returns null when the value isn't a usable
 * range. Applied at merge time so interactive-run values (which bypass
 * `resolveParameterBindings`) normalize identically to scheduled bindings.
 */
function toRangeTuple(value: unknown): [string, string] | null {
  let tuple: [unknown, unknown] | null = null;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    const s = o.start ?? o.startDate ?? o.from;
    const e = o.end ?? o.endDate ?? o.to;
    if (s !== undefined || e !== undefined) tuple = [s, e];
  } else if (typeof value === 'string') {
    // 'YYYY-MM-DD.YYYY-MM-DD…' joins: split on a '.' immediately before the
    // second ISO date (ms fractions never precede a 4-digit year).
    const m = value.match(/^(.+?)\.(\d{4}-\d{2}-\d{2}.*)$/);
    if (m) tuple = [m[1], m[2]];
  } else if (Array.isArray(value) && value.length === 2) {
    tuple = [value[0], value[1]];
  }
  if (!tuple) return null;
  const [s, e] = tuple;
  return typeof s === 'string' && typeof e === 'string' && toDateMs(s) !== null && toDateMs(e) !== null
    ? [s, e]
    : null;
}

function normalizeParamValue(param: ReportParameter, value: unknown): unknown {
  if (param.type !== 'date' && param.type !== 'dateRange') return value;
  const tuple = toRangeTuple(value);
  if (!tuple) return value;
  return param.type === 'date' ? tuple[0] : tuple;
}

/**
 * Validate schedule parameter bindings against a report's declared parameters.
 * Returns an array of validation error strings (empty when valid).
 *
 * @process validateScheduleParameterBindings
 * Steps:   1. Verify each binding has a valid mode and required value/token.
 *          2. Verify every required parameter in `parameters` can resolve.
 * Inputs:  bindings, parameters.
 * Outputs: string[] (empty = valid).
 * Failure Handling: Pure validation — returns error messages without throwing.
 * Idempotency: REQUIRED — pure function.
 */
export function validateScheduleParameterBindings(
  bindings: ParameterBinding[],
  parameters: ReportParameter[],
): string[] {
  const errors: string[] = [];
  for (const b of bindings) {
    if (b.mode === 'fixed') {
      if (
        b.value === undefined ||
        b.value === null ||
        b.value === '' ||
        (Array.isArray(b.value) && b.value.length === 0)
      ) {
        errors.push(`Fixed binding for '${b.parameterId}' requires a non-empty value`);
      }
    } else if (b.mode === 'relative') {
      if (!b.token) {
        errors.push(`Relative binding for '${b.parameterId}' requires a token`);
      }
    }
  }
  try {
    resolveParameterBindings(bindings, parameters, new Date());
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (!errors.includes(msg)) {
      errors.push(msg);
    }
  }
  return errors;
}

/**
 * Merge static report filters with resolved parameter values (and optional
 * column-level `parameterId` bindings).
 * Filters with `parameterId` take their value from the resolved map; a
 * parameterId filter with no resolved value is dropped (parameter not supplied).
 * Static filters (no parameterId) pass through verbatim.
 * Any column carrying `column.parameterId` without an existing matching
 * `(field, parameterId)` filter entry is synthesized into the merged filter list.
 *
 * Any parameter carrying `parameter.filterField` without an existing matching
 * `(filterField, parameterId)` filter entry is likewise synthesized — this is
 * how bare runtime parameters (no column/filter binding) still constrain the
 * query.
 *
 * @process mergeFiltersWithParameters
 * Inputs: staticFilters (reportConfig.filters), resolvedValues, columns (optional),
 *   parameters (optional — enables filterField synthesis).
 * Outputs: A flat filter list ready for the data gatherer.
 * Failure Handling: parameterId filters referencing unknown parameters are dropped
 *   (never silently filter on a stale static value).
 * Idempotency: REQUIRED — pure function.
 */
export function mergeFiltersWithParameters(
  staticFilters: ReportConfigFilter[],
  resolvedValues: ResolvedParameterValues,
  columns?: Array<{ field: string; parameterId?: string }>,
  parameters?: ReadonlyArray<Pick<ReportParameter, 'parameterId' | 'filterField'>>,
): Array<{ field: string; operator: string; value: any }> {
  const out: Array<{ field: string; operator: string; value: any }> = [];
  const boundColumnKeys = new Set<string>();

  for (const filter of staticFilters) {
    if (filter.parameterId) {
      boundColumnKeys.add(`${filter.field}::${filter.parameterId}`);
      const resolved = resolvedValues.values[filter.parameterId];
      if (resolved === undefined) continue;
      const v = toRangeTuple(resolved) ?? resolved;
      out.push({
        field: filter.field,
        operator: inferParameterOperator(filter.operator, v),
        value: v,
      });
      continue;
    }
    out.push({ field: filter.field, operator: filter.operator, value: filter.value });
  }

  if (Array.isArray(columns)) {
    for (const col of columns) {
      if (!col.parameterId || !col.field) continue;
      const key = `${col.field}::${col.parameterId}`;
      if (boundColumnKeys.has(key)) continue;
      boundColumnKeys.add(key);

      const resolved = resolvedValues.values[col.parameterId];
      if (resolved === undefined) continue;

      const v = toRangeTuple(resolved) ?? resolved;
      const operator = inferParameterOperator('eq', v);

      out.push({ field: col.field, operator, value: v });
    }
  }

  if (Array.isArray(parameters)) {
    for (const param of parameters) {
      if (!param.filterField) continue;
      const key = `${param.filterField}::${param.parameterId}`;
      if (boundColumnKeys.has(key)) continue;
      boundColumnKeys.add(key);

      const resolved = resolvedValues.values[param.parameterId];
      if (resolved === undefined) continue;

      const v = toRangeTuple(resolved) ?? resolved;
      out.push({
        field: param.filterField,
        operator: inferParameterOperator('eq', v),
        value: v,
      });
    }
  }

  return out;
}

function isDateRangeTupleValue(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'string' &&
    typeof value[1] === 'string' &&
    toDateMs(value[0]) !== null &&
    toDateMs(value[1]) !== null
  );
}

/**
 * Infer the effective operator for a parameter-driven filter. Explicit
 * non-equality operators (inDateRange, in, between, …) pass through verbatim;
 * `eq`-family operators are upgraded to match the resolved value's shape so a
 * dateRange parameter stored on an `eq` filter does not compare a field value
 * against a `[start, end]` array (which can never match). Returns 'eq' for
 * scalar values.
 */
function inferParameterOperator(storedOperator: string | undefined, resolvedValue: unknown): string {
  const op = (storedOperator ?? 'eq').toLowerCase();
  const isEqFamily = op === 'eq' || op === '=' || op === 'equals';
  if (!isEqFamily) return storedOperator ?? 'eq';
  if (isDateRangeTupleValue(resolvedValue)) return 'inDateRange';
  if (Array.isArray(resolvedValue)) return 'in';
  return storedOperator ?? 'eq';
}

/**
 * Evaluate a flat filter list (the output of mergeFiltersWithParameters, or
 * static reportConfig.filters) against a flattened data row (e.g.
 * `{ 'cases.caseNumber': 'FIRE-001' }`). Shared by the local-first preview path
 * (Dexie rows) and the worker's scheduled-run gatherer — one implementation, no
 * client/server mirror.
 *
 * Supported operators: eq (default), ne, contains, startsWith, endsWith, in,
 * nin, gt, gte, lt, lte, inDateRange / between (value = [startIso, endIso]),
 * isNull, isNotNull. Unknown operators fail closed (row excluded) so a stale
 * saved operator never silently broadens a report.
 *
 * @reusable rowMatchesFilters
 * Rules: Pure function; string comparisons for `contains` are case-insensitive;
 *   date comparisons coerce both sides through Date.
 * @errorHandling Strategy: unknown operator → row excluded; User Impact: report
 *   shows fewer rows rather than wrong rows; Logging: none (pure).
 */
/**
 * Lookup-aware equality for equality-family operators. The shared flattener
 * emits a lookup-reference's display name at `field` and its code at
 * `field.code` — a filter matches when the expected value equals either, so a
 * lookup parameter resolving to 'active' matches `status: {name: 'Active',
 * code: 'active'}` projected as `field='Active'`, `field.code='active'`.
 */
function rowFieldEquals(
  row: Record<string, unknown>,
  field: string,
  expected: unknown,
): boolean {
  const parent = row[field];
  if (parent === expected) return true;
  const code = row[`${field}.code`];
  return code !== undefined && code === expected;
}

export function rowMatchesFilters(
  row: Record<string, unknown>,
  filters: Array<{ field: string; operator: string; value: any }>,
): boolean {
  for (const filter of filters) {
    const rowValue = row[filter.field];
    const op = (filter.operator ?? 'eq').toLowerCase();

    switch (op) {
      case 'eq': case '=': case 'equals':
        if (!rowFieldEquals(row, filter.field, filter.value)) return false;
        break;
      case 'ne': case '!=': case 'notequals':
        if (rowFieldEquals(row, filter.field, filter.value)) return false;
        break;
      case 'contains': {
        if (rowValue === undefined || rowValue === null) return false;
        if (!String(rowValue).toLowerCase().includes(String(filter.value).toLowerCase())) return false;
        break;
      }
      case 'startswith':
        if (rowValue === undefined || !String(rowValue).toLowerCase().startsWith(String(filter.value).toLowerCase())) return false;
        break;
      case 'endswith':
        if (rowValue === undefined || !String(rowValue).toLowerCase().endsWith(String(filter.value).toLowerCase())) return false;
        break;
      case 'in': {
        const list = Array.isArray(filter.value) ? filter.value : [filter.value];
        if (!list.some((v) => rowFieldEquals(row, filter.field, v))) return false;
        break;
      }
      case 'nin': {
        const list = Array.isArray(filter.value) ? filter.value : [filter.value];
        if (list.some((v) => rowFieldEquals(row, filter.field, v))) return false;
        break;
      }
      case 'gt': if (!(toComparable(rowValue) > toComparable(filter.value))) return false; break;
      case 'gte': if (!(toComparable(rowValue) >= toComparable(filter.value))) return false; break;
      case 'lt': if (!(toComparable(rowValue) < toComparable(filter.value))) return false; break;
      case 'lte': if (!(toComparable(rowValue) <= toComparable(filter.value))) return false; break;
      case 'indaterange': case 'between': {
        const range = filter.value;
        if (!Array.isArray(range) || range.length < 2) return false;
        const v = toDateMs(rowValue);
        const start = toDateMs(range[0]);
        let end = toDateMs(range[1]);
        if (v === null || start === null || end === null) return false;
        // Date-only end strings ('YYYY-MM-DD') represent the entire final day through 23:59:59.999.
        if (typeof range[1] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(range[1].trim())) {
          end += DAY_MS - 1;
        }
        if (v < start || v > end) return false;
        break;
      }
      case 'isnull':
        if (rowValue !== undefined && rowValue !== null && rowValue !== '') return false;
        break;
      case 'isnotnull':
        if (rowValue === undefined || rowValue === null || rowValue === '') return false;
        break;
      default:
        return false; // fail closed on unknown operators
    }
  }
  return true;
}

/** Coerce a value for ordering comparisons (dates → epoch ms, else primitive). */
function toComparable(v: unknown): number | string {
  if (v === undefined || v === null) return '';
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  const ms = toDateMs(v);
  if (ms !== null && typeof v === 'string' && v.length >= 8) return ms;
  return String(v);
}

/** Coerce a value to epoch ms, or null when not date-like. */
function toDateMs(v: unknown): number | null {
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/**
 * Validate a report's parameter declarations. Pure — used by the service layer
 * (saveReportParameters) and reusable by any host validating a parameter draft.
 *
 * @param parameters - Draft parameter array.
 * @returns Error strings (empty = valid).
 */
const VALID_RELATIVE_TOKENS: ReadonlySet<RelativeDateToken> = new Set([
  'previousMonth', 'currentMonth', 'nextMonth', 'previousQuarter', 'currentQuarter',
  'last30Days', 'last7Days', 'yearToDate', 'previousYear', 'currentYear',
  'previousDay', 'currentDay',
]);

export function validateReportParameters(parameters: ReportParameter[]): string[] {
  const errors: string[] = [];
  const seen = new Set<string>();

  for (const param of parameters) {
    if (!param.parameterId?.trim()) {
      errors.push('Every parameter needs a non-empty parameterId');
      continue;
    }
    if (seen.has(param.parameterId)) {
      errors.push(`Duplicate parameterId '${param.parameterId}'`);
    }
    seen.add(param.parameterId);

    if (!param.label?.trim()) {
      errors.push(`Parameter '${param.parameterId}' needs a label`);
    }
    if (param.mode === 'relative' && param.type !== 'date' && param.type !== 'dateRange') {
      errors.push(`Parameter '${param.parameterId}' (type '${param.type}') cannot use relative mode`);
    }
    if (
      param.mode === 'relative' &&
      param.defaultValue !== undefined &&
      !VALID_RELATIVE_TOKENS.has(param.defaultValue as RelativeDateToken)
    ) {
      errors.push(`Relative parameter '${param.parameterId}' defaultValue must be a relative date token`);
    }
    if (param.type === 'lookup' && !param.options?.lookupType?.trim()) {
      errors.push(`Lookup parameter '${param.parameterId}' requires options.lookupType`);
    }
  }

  return errors;
}
